/**
 * F-08B — Driver route lockdown (UI/navigation layer only).
 *
 * Drivers may only open the operational routes listed here; any other route
 * inside the authenticated app shell redirects to the driver landing page.
 * This NEVER replaces RLS / Edge Function authorisation — it only stops
 * drivers landing on admin screens via direct URL, refresh, bookmarks,
 * deep links, push taps or back/forward history.
 */

export const DRIVER_LANDING_PATH = "/dashboard";

/** Exact paths a driver may open. */
const DRIVER_EXACT_PATHS = new Set<string>([
  "/dashboard",
  "/jobs",
  "/pending-jobs",
  "/schedule", // role-selected: drivers get My Schedule (F-08)
  "/offline-jobs",
  "/pending-uploads",
  "/vehicle-daily-checks",
  "/my-messages",
  "/driver-settings",
  "/more",
  "/shortcuts", // role-aware native shortcuts (driver set only)
  "/expenses", // driver's own expense/receipt view; admin view is desktop-admin only
  "/help-guides",
  "/feedback",
  "/report-problem",
]);

/** Prefixes for parameterised driver routes (`/prefix/:id`). */
const DRIVER_PARAM_PREFIXES = [
  "/job/", // assigned job details (job-level RLS still applies)
  "/resume-job/", // POC / POD workflow
  "/appraisal/", // appraisal viewer for assigned jobs
  "/offline-job/",
];

function normalise(pathname: string): string {
  const p = (pathname || "/").split(/[?#]/)[0];
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

export function isDriverAllowedPath(pathname: string): boolean {
  const p = normalise(pathname);
  if (DRIVER_EXACT_PATHS.has(p)) return true;
  return DRIVER_PARAM_PREFIXES.some((prefix) => {
    if (!p.startsWith(prefix)) return false;
    const rest = p.slice(prefix.length);
    // exactly one non-empty segment, e.g. /job/<id> but not /job/<id>/print
    return rest.length > 0 && !rest.includes("/");
  });
}

export type DriverRouteDecision = "allow" | "wait" | "redirect";

/**
 * Pure decision. Driver-allowed paths render immediately (so offline POC/POD
 * never waits on role resolution); other paths wait for the role, then
 * redirect only confirmed drivers. Admin/owner/Super Admin are unaffected.
 */
export function decideDriverRoute(input: {
  pathname: string;
  roleLoading: boolean;
  isDriver: boolean;
}): DriverRouteDecision {
  if (isDriverAllowedPath(input.pathname)) return "allow";
  if (input.roleLoading) return "wait";
  return input.isDriver ? "redirect" : "allow";
}
